import {
  DocumentReference,
  isDocRef,
  isObject,
  ISODateType,
  TypeGuard,
} from '@principle-theorem/shared';
import { isBoolean } from 'lodash';
import { NZ_SCHOOLS } from '../../clinical-charting/service-provider/providers/moh/nz-schools';
import { IClaimableItem } from '../../invoice/healthcare-claim';
import { IInvoice } from '../../invoice/invoice';
import { MohFundingRequest } from '../funding-request/funding-request';
import { IMohPreApprovalFundingRequest } from '../funding-request/moh/moh-pre-approval';
import { IMohHighCariesData } from './high-caries';
import { IMohPreApprovalData } from './moh-pre-approval';
import {
  IMohOhsaClaimSummaryData,
  IMohOhsaIndividualTreatmentData,
} from './ohsa';
import { IMohSdsIndividualTreatmentData, IMohSdsSummaryData } from './sds';

export const DEFAULT_MOH_CLAIM_EMAIL = 'cdaclaims@health.govt.nz';
export const DEFAULT_MOH_ENROLMENT_EMAIL = 'customerservice@health.govt.nz';

export enum MohClaimType {
  OHSA = 'ohsa',
  SDS = 'sds',
  HighCaries = 'highCaries',
}

export enum MohPdfFormName {
  OhsaIndividualTreatment = 'ohsa-individual-treatment',
  OhsaClaimSummary = 'ohsa-claim-summary',
  SdsIndividualTreatment = 'sds-individual-treatment',
  SdsClaimSummary = 'sds-claim-summary',
  PreApproval = 'pre-approval',
  HighCaries = 'high-caries',
}

export const MOH_CLAIM_TYPE_LABEL_MAP: Record<MohClaimType, string> = {
  [MohClaimType.OHSA]: 'OHSA',
  [MohClaimType.SDS]: 'SDS',
  [MohClaimType.HighCaries]: 'High Caries',
};

export const MOH_DATE_FORMAT = 'DD/MM/YY';
export const MOH_SHORT_DATE_FORMAT = 'DDMMYY';

export interface IMohServiceCodeExtendedData {
  inStandardPackage?: boolean;
  preApprovalClaimTypes?: MohClaimType[];
  claimTypes?: MohClaimType[];
}

export const isMohServiceCodeExtendedData =
  TypeGuard.interface<IMohServiceCodeExtendedData>({
    inStandardPackage: TypeGuard.undefinedOr(isBoolean),
    preApprovalClaimTypes: TypeGuard.undefinedOr(
      TypeGuard.arrayOf(TypeGuard.enumValue(MohClaimType))
    ),
    claimTypes: TypeGuard.undefinedOr(
      TypeGuard.arrayOf(TypeGuard.enumValue(MohClaimType))
    ),
  });

export interface IMohClaimableItem extends IClaimableItem {
  comments?: string;
}

export interface IMohPdfTreatment {
  code: string;
  quantity: number;
  price: number;
  comments: string;
  teeth: string;
  date?: ISODateType;
}

export interface IMohFormDisplay {
  formPath: string;
  label: MohFormDisplayLabel;
}

export enum MohFormDisplayLabel {
  IndividualTreatment = 'individualTreatment',
  ClaimSummary = 'claimSummary',
  PreApprovalRequest = 'preApprovalRequest',
  HighCariesRequest = 'highCariesRequest',
  PatientEnrolment = 'patientEnrolment',
}

interface IBaseMohClaimData {
  invoiceRef: DocumentReference<IInvoice>;
  claimUid: string;
  approvalRequestRef?: DocumentReference<IMohPreApprovalFundingRequest>;
}

export interface IMohOhsaClaimData extends IBaseMohClaimData {
  summaryData: IMohOhsaClaimSummaryData;
  treatmentData: IMohOhsaIndividualTreatmentData;
}

export interface IMohSdsClaimData extends IBaseMohClaimData {
  summaryData: IMohSdsSummaryData;
  treatmentData: IMohSdsIndividualTreatmentData;
}

export enum MohGenerateFormRequestType {
  OhsaClaim = 'ohsaClaim',
  SdsClaim = 'sdsClaim',
  PreApproval = 'PreApproval',
  HighCaries = 'highCaries',
}

interface IMohGenerateFormRequestDataMap {
  [MohGenerateFormRequestType.OhsaClaim]: IMohOhsaClaimData;
  [MohGenerateFormRequestType.SdsClaim]: IMohSdsClaimData;
  [MohGenerateFormRequestType.PreApproval]: IMohPreApprovalData;
  [MohGenerateFormRequestType.HighCaries]: IMohPreApprovalData & {
    highCaries: IMohHighCariesData;
  };
}

export type MohGenerateFormRequest = {
  [K in MohGenerateFormRequestType]: {
    type: K;
    requestRef: DocumentReference<MohFundingRequest>;
    data: IMohGenerateFormRequestDataMap[K];
  };
}[MohGenerateFormRequestType];

export const isMohGenerateFormRequest =
  TypeGuard.interface<MohGenerateFormRequest>({
    type: TypeGuard.enumValue(MohGenerateFormRequestType),
    requestRef: isDocRef,
    data: isObject,
  });

export interface IMohSchool {
  schoolNumber: number;
  schoolName: string;
  eqiNumber?: number;
}

export const isMohSchool = TypeGuard.interface<IMohSchool>({
  schoolNumber: TypeGuard.isNumber,
  schoolName: TypeGuard.isString,
  eqiNumber: TypeGuard.undefinedOr(TypeGuard.isNumber),
});

export const MOH_NZ_SCHOOL_MAP: Record<number, IMohSchool> = NZ_SCHOOLS.reduce(
  (previous, school) => {
    return {
      ...previous,
      [school.schoolNumber]: school,
    };
  },
  {} as Record<number, IMohSchool>
);
